import {
  ACTION_CLICK_BUTTON_EXPORT_ICAL_APPLE,
  ACTION_CLICK_BUTTON_EXPORT_TO_GOOGLE_CALENDAR,
  ACTION_CLICK_BUTTON_EXPORT_TO_OUTLOOK_CALENDAR,
  ACTION_CLICK_LINK_EXPORT_ICAL,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { CalendarEvent, google, ics, outlook } from 'calendar-link';
import {
  Dialog,
  DialogContent,
  DialogHeading,
  Divider,
  IconButton,
  Link,
  Typography,
} from 'omakase';
import { IconLogoApple, IconLogoGoogle, IconLogoOutlook } from '@tock/icons';
import { Theme, css } from '@emotion/react';
import { core } from '@tock/coretypes';
import { experienceToData } from 'utils/analytics/amplitude';
import { pluralize } from '@tock/shared/utils/pluralize';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import Anchor from 'components/Anchor';
import React from 'react';

interface Props {
  purchase: core.PastPurchase;
  patron: core.Patron;
  onClose: () => void;
  isOpen: boolean;
}

const CalendarExportModal: React.FunctionComponent<Props> = ({
  purchase,
  patron,
  onClose,
  isOpen = false,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const event: CalendarEvent = {
    title: `Reservation at ${purchase.business?.name}`,
    description: `${
      purchase.ticketCount ? pluralize(purchase.ticketCount, 'guest') : 'Reservation'
    } for ${patron.firstName} ${patron.lastName}`,
    start: purchase.ticketDateTime,
    duration: [2, 'hours'],
  };

  const amplitudeEvent: EventCustomAttributes = {
    businessId: purchase.business?.id ?? undefined,
    businessName: purchase.business?.name ?? undefined,
    reservationDateTime: purchase.ticketDateTime,
    ...experienceToData(purchase.ticketType || undefined),
  };

  const handleGoogleCalendarExport = () => {
    amplitudeSendEvent(ACTION_CLICK_BUTTON_EXPORT_TO_GOOGLE_CALENDAR, amplitudeEvent);
  };

  const handleAppleCalendarExport = () => {
    amplitudeSendEvent(ACTION_CLICK_BUTTON_EXPORT_ICAL_APPLE, amplitudeEvent);
  };

  const handleOutlookCalendarExport = () => {
    amplitudeSendEvent(ACTION_CLICK_BUTTON_EXPORT_TO_OUTLOOK_CALENDAR, amplitudeEvent);
  };

  const handleIcsCalendarExport = () => {
    amplitudeSendEvent(ACTION_CLICK_LINK_EXPORT_ICAL, amplitudeEvent);
  };

  return (
    <Dialog
      aria-labelledby="calendar-export-dialog-title"
      aria-describedby="calendar-export-dialog-content"
      onClose={onClose}
      open={isOpen}
      variant="sheet"
    >
      <DialogHeading
        onClose={onClose}
        headingConfig={{
          right: {
            configType: 'closeButton',
          },
          left: { configType: 'title', title: 'Add to calendar' },
        }}
      ></DialogHeading>
      <DialogContent>
        <div
          css={(theme: Theme) => css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: ${theme.spacing(6)}px 0px;

            ${theme.breakpoints.up('sm')} {
              padding: ${theme.spacing(2.5)}px 0px;
            }
          `}
        >
          <Typography variant="body01Strong" mb={1}>
            Reservation at {purchase.business?.name}
          </Typography>
          <Typography variant="body02" mb={3}>
            Add this reservation to your calendar by choosing one of the options below.
          </Typography>
          <div
            css={(theme: Theme) => css`
              margin-bottom: ${theme.spacing(3)}px;
              display: flex;

              & * + * {
                margin-left: ${theme.spacing(2)}px;
              }
            `}
          >
            <IconButton
              aria-label="Export calendar event to Google"
              component={Anchor}
              onClick={handleGoogleCalendarExport}
              size="medium"
              variant="basic"
              href={google(event)}
              target="_blank"
            >
              <IconLogoGoogle />
            </IconButton>
            <IconButton
              aria-label="Export calendar event as ICS file"
              component={Anchor}
              onClick={handleAppleCalendarExport}
              size="medium"
              variant="basic"
              href={ics(event)}
              target="_blank"
              allowDataProtocolUrl={true}
            >
              <IconLogoApple />
            </IconButton>
            <IconButton
              aria-label="Export calendar event to Outlook"
              component={Anchor}
              onClick={handleOutlookCalendarExport}
              size="medium"
              variant="basic"
              href={outlook(event)}
              target="_blank"
            >
              <IconLogoOutlook />
            </IconButton>
          </div>
          <div
            css={(theme: Theme) => css`
              display: flex;
              width: 100%;
              position: relative;
              justify-content: center;
              margin-bottom: ${theme.spacing(3)}px;
            `}
          >
            <Divider
              css={(theme: Theme) => css`
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
              `}
            />
            <Typography
              variant="overline"
              color="fgSubtle"
              css={(theme: Theme) => css`
                padding: ${theme.spacing(0)}px ${theme.spacing(2)}px;
                background-color: ${theme.colors.bgDefault};
                z-index: 1;
              `}
            >
              OR
            </Typography>
          </div>
          <Link
            onClick={handleIcsCalendarExport}
            href={ics(event)}
            target="_blank"
            allowDataProtocolUrl={true}
          >
            Download invite (.ICS)
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarExportModal;
